import React, { useCallback, useMemo, useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { ClearStoreSmButton } from 'features/store/pages/pickup-store-selector/components/clear-store-sm-button';
import { Platform, StyleSheet, TouchableOpacity } from 'react-native';

import { ILocation } from '@rbi-ctg/frontend';
import { CardModal } from 'components/card-modal';
import { Box } from 'components/ui';
import { useIsDesktopBreakpoint, useIsLowHeightDevice } from 'hooks/use-media-query';
import { useGeolocation } from 'state/geolocation';
import { t } from 'state/intl/translations';
import { OrderLocatorTab } from 'state/launchdarkly/variations';
import { getDesignToken, spacing } from 'styles/constants';
import { IPlaceData } from 'utils/geolocation';

import { ListView, SearchBar, StoreChangeDisclaimer } from './components';
import { CancelSearchIcon } from './components/header/cancel-search-icon';
import { MapView } from './components/map-view/map.view';
import { SelectRestaurantFooter } from './components/select-restaurant-footer';
import { IStoreLocatorViewProps } from './components/types';

/**
 * This is the main component for the store locator.
 * It is responsible for handling the state of the store locator and the logic for the map and list view.
 */
export const StoreLocatorView: React.FC<IStoreLocatorViewProps> = ({
  searchingNearby,
  searchingRecent,
  searchingFavs,
  storesByTab,
  storesById,
  isInSupportForm,
  nearbyOnly,
  isOffersSelection,
  headerText,
  actions,
  currentTabKey,
  currentTabIndex,
  currentTabHasError,
  focusedRestaurant,
  locatorTabs,
  activeStore,
  allStores,
  flatListRef,
  mapRef,
  closestStore,
  onTabChange,
  onSelectStore,
  onDismissCallback,
  onFavoriteClick,
  onSearchThisArea,
  onResetUserCoords,
  onRestaurantHighlight,
  onResetManualSearch,
  onAddressSelected,
  isStoreEligible,
  ineligibleStoreMessage,
}) => {
  const isLowHeightDevice = useIsLowHeightDevice();
  const isDesktop = useIsDesktopBreakpoint();
  const isMinimalPicker = !!nearbyOnly || !!isOffersSelection;

  const { isPermissionGranted } = useGeolocation();

  const [searchFieldActive, setSearchFieldActive] = useState<boolean>(
    !isPermissionGranted || isMinimalPicker
  );
  const showSearchBar = searchFieldActive || isInSupportForm || isMinimalPicker;

  const nearbyStores = storesByTab[OrderLocatorTab.NEARBY] ?? [];
  const preferredOffersRestaurant =
    focusedRestaurant && (!isStoreEligible || isStoreEligible(focusedRestaurant))
      ? focusedRestaurant
      : nearbyStores.find(store => (isStoreEligible ? isStoreEligible(store) : true)) ??
        closestStore ??
        nearbyStores[0];
  const selectedOffersRestaurantId = preferredOffersRestaurant?._id ?? undefined;
  const selectedOffersRestaurant = selectedOffersRestaurantId
    ? storesById[selectedOffersRestaurantId]
    : undefined;
  const currentRestaurant = activeStore?._id ? storesById[activeStore._id] ?? null : null;
  const canContinueWithCurrentRestaurant = Boolean(
    currentRestaurant?._id &&
      (!isStoreEligible || (activeStore ? isStoreEligible(activeStore) : false))
  );

  const onToggleSearchField = useCallback(() => {
    setSearchFieldActive(prevSearchFieldActive => {
      // When the user press the search field, we need to change the tab to the nearby tab
      if (currentTabKey !== OrderLocatorTab.NEARBY && prevSearchFieldActive === false) {
        onTabChange(OrderLocatorTab.NEARBY);
      }
      return !prevSearchFieldActive;
    });
  }, [currentTabKey, onTabChange]);

  const onPressSearchThisArea = useCallback(
    (location: ILocation) => {
      onTabChange(OrderLocatorTab.NEARBY);
      onSearchThisArea(location);
    },
    [onSearchThisArea, onTabChange]
  );

  const tabSearchStatusMap = useMemo<{ [K in OrderLocatorTab]: boolean }>(
    () => ({
      [OrderLocatorTab.NEARBY]: searchingNearby,
      [OrderLocatorTab.RECENT]: searchingRecent,
      [OrderLocatorTab.FAVORITE]: searchingFavs,
      [OrderLocatorTab.DELIVERY]: false,
    }),
    [searchingNearby, searchingRecent, searchingFavs]
  );

  const handleOnCenterToUserLocation = useCallback(async () => {
    if (!isPermissionGranted) {
      return;
    }
    onResetUserCoords();
  }, [isPermissionGranted, onResetUserCoords]);

  const handleOnAddressSelected = useCallback(
    (address: string, placeId?: string, _placeData?: IPlaceData) => {
      if (address) {
        onAddressSelected(address, placeId);
      } else {
        onResetManualSearch();
      }
    },
    [onAddressSelected, onResetManualSearch]
  );

  return (
    <CardModal
      testID="store-locator-wrapper"
      headerText={headerText ?? t('selectLocation')}
      alwaysHideHeader={isInSupportForm}
      invertHeaderColor
      onDismiss={onDismissCallback}
      hasInternalFooter={!!isOffersSelection}
      customHeaderLeft={
        <TouchableOpacity testID="search-store-sm-button" onPress={onToggleSearchField}>
          {searchFieldActive ? (
            <CancelSearchIcon aria-hidden />
          ) : (
            <Icon variant="search" color={getDesignToken('text-button-reversed')} aria-hidden />
          )}
        </TouchableOpacity>
      }
    >
      <Box style={styles.topSelectionContainer}>
        <Box style={[styles.searchContainer, showSearchBar && styles.searchContainerWithSearchBar]}>
          {showSearchBar && (
            <Box style={[styles.searchBarWrapper, isDesktop && styles.searchBarWrapperDesktop]}>
              <SearchBar onAddressSelected={handleOnAddressSelected} />
            </Box>
          )}
        </Box>
      </Box>

      {!isOffersSelection ? (
        <Box>
          <StoreChangeDisclaimer marginX="$4" marginTop="$2" marginBottom="$4" />
        </Box>
      ) : null}

      <Box style={styles.contentContainer}>
        {!isOffersSelection ? <ClearStoreSmButton /> : null}
        {/* If is lowHeightDevice, the map will take up the middle of the screen.
         * Otherwise, the map will take up 2/3 of the screen and the list will take up 1/3 of the screen.
         * This is a workaround to fix the issue with the empty states into the list view is not being visible on some devices.
         * Offers / minimal picker: equal map:list flex (same as low-height devices). Without the tab bar,
         * tallMapWrapper (1.5) makes the map look larger than the regular locator.
         */}
        <Box
          style={[
            styles.mapWrapper,
            !isLowHeightDevice && !isOffersSelection && styles.tallMapWrapper,
          ]}
        >
          <MapView
            onMarkerPress={onRestaurantHighlight}
            onSelectStore={onSelectStore}
            onCenterToUserLocation={handleOnCenterToUserLocation}
            onPressSearchThisArea={onPressSearchThisArea}
            stores={allStores}
            storesFavs={storesByTab[OrderLocatorTab.FAVORITE]}
            restaurantFocused={focusedRestaurant}
            isInSupportForm={isInSupportForm}
            actions={actions}
            closestStore={closestStore}
            activeStore={activeStore}
            ref={mapRef}
            searchingNearby={searchingNearby}
            isStoreEligible={isStoreEligible}
          />
        </Box>
        <Box style={[styles.listWrapper, isOffersSelection && styles.offersListWrapper]}>
          <ListView
            onRestaurantHighlight={onRestaurantHighlight}
            onSelectStore={onSelectStore}
            onTabChange={onTabChange}
            onFavoriteClick={onFavoriteClick}
            locatorTabs={locatorTabs}
            storesByTab={storesByTab}
            isInSupportForm={isInSupportForm}
            nearbyOnly={nearbyOnly}
            isOffersSelection={isOffersSelection}
            restaurantFocused={focusedRestaurant ?? null}
            currentTabKey={currentTabKey}
            currentTabIndex={currentTabIndex}
            currentTabHasError={currentTabHasError}
            flatListRef={flatListRef}
            storesById={storesById}
            actions={actions}
            tabSearchStatusMap={tabSearchStatusMap}
            isStoreEligible={isStoreEligible}
            activeStore={activeStore}
            ineligibleStoreMessage={ineligibleStoreMessage}
          />
        </Box>
        {isOffersSelection ? (
          <SelectRestaurantFooter
            restaurant={selectedOffersRestaurant}
            onSelectStore={onSelectStore}
            isEligible={
              preferredOffersRestaurant && isStoreEligible
                ? isStoreEligible(preferredOffersRestaurant)
                : true
            }
            currentRestaurant={currentRestaurant}
            canContinueWithCurrentRestaurant={canContinueWithCurrentRestaurant}
          />
        ) : null}
      </Box>
    </CardModal>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    flex: 1,
  },
  listWrapper: {
    flex: 1,
    minHeight: 0,
  },
  mapWrapper: {
    flex: 1,
    minHeight: 0,
  },
  /** Slightly prefer list space when the tab bar is hidden (offers picker). */
  offersListWrapper: {
    flex: 1.25,
  },
  searchBarWrapper: {
    height: spacing.$12,
    marginBottom: spacing.$0,
    marginTop: spacing.$4,
    paddingHorizontal: spacing.$4,
    width: '100%',
  },
  searchBarWrapperDesktop: {
    paddingHorizontal: spacing.$0,
  },
  searchContainer: {
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  searchContainerWithSearchBar: {
    marginBottom: spacing.$4,
  },
  tallMapWrapper: {
    flex: 1.5,
  },
  topSelectionContainer: {
    ...Platform.select({
      ios: { zIndex: 1 },
      web: { zIndex: 1 },
      default: {},
    }),
  },
});
